import { useCallback, useMemo } from 'react';

import useUser from 'src/hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useModals, ModalNames } from 'components/modals';
import {
  useOrdersForConsumer,
  UseOrdersForConsumerReturn,
} from 'src/components/embedded-account/content/order-list/use-orders-for-consumer/use-orders-for-consumer';
import { useReorder } from 'src/hooks/use-reorder';
import { ConsumerOrder } from 'src/components/embedded-account/account.types';
import { isNotNullish } from 'shared/utils/type-utils';

export type WidgetOrder = NonNullable<UseOrdersForConsumerReturn['orders'][number]>;

export type UseWidgetOrdersReturn = {
  orders: WidgetOrder[];
  ordersLoading: boolean;
  reorder: ((order: ConsumerOrder) => Promise<void>) | undefined;
  loadingOrderId: string | null;
  handleViewOrder: (order: WidgetOrder) => void;
};

export function useWidgetOrders(): UseWidgetOrdersReturn {
  const { dispensary } = useDispensary();
  const user = useUser();
  const { openModal } = useModals();
  const { reorder, loadingOrderId } = useReorder();
  const isEnabled = reorder !== undefined;
  const { orders, loading: rawOrdersLoading } = useOrdersForConsumer({ dispensary, skip: !isEnabled });
  const ordersLoading = isEnabled && rawOrdersLoading;

  const handleViewOrder = useCallback(
    (order: WidgetOrder): void => {
      user.viewOrderId = order.orderId;
      void openModal(ModalNames.orderReceipt);
    },
    [user, openModal]
  );

  const nonNullOrders = useMemo(() => orders.filter(isNotNullish), [orders]);

  return {
    orders: nonNullOrders,
    ordersLoading,
    reorder,
    loadingOrderId,
    handleViewOrder,
  };
}
